import React from 'react';
import { ArrowUpRight, Globe } from 'lucide-react';

/**
 * "Part of the Paisley network" — Liquid Glass card row.
 * Four-tile network:
 *   1. paisley.org.uk    (this site)
 *   2. paisleyscotland.com  (sister lifestyle / business directory + jobs)
 *   3. paisleymurals.co.uk  (Paisley Murals + Mural Trail)
 *   4. clydeflow.com        (productivity / workflow sister product)
 *
 * Tiles 3 and 4 use their real brand logos served from /network-logos/.
 * Tiles 1 and 2 use lucide icons since no logo file exists yet.
 */
const NetworkBadge = () => (
  <section className="relative py-20" data-testid="network-badge">
    <div className="aurora-orb w-[420px] h-[420px] top-0 left-1/3 bg-sky-300/40" />

    <div className="relative max-w-7xl mx-auto px-6">
      <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-sky-600 mb-3">Our network</p>
        <h2 className="text-4xl md:text-5xl font-bold tracking-[-0.04em] text-slate-900 leading-none">
          Part of the <span className="font-serif-accent italic font-light bg-gradient-to-r from-sky-500 to-indigo-500 bg-clip-text text-transparent">Paisley</span> network
        </h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* 1 — paisley.org.uk (current site) */}
        <div className="glass-strong rounded-3xl p-7 text-center" data-testid="network-paisley-org-card">
          <div className="w-14 h-14 rounded-2xl mx-auto bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-sky-500/30 ring-1 ring-white/40 mb-4">
            <Globe size={22} className="text-white" />
          </div>
          <p className="text-lg font-bold text-slate-900 tracking-tight">paisley<span className="text-sky-500">.</span>org<span className="text-slate-400">.uk</span></p>
          <p className="text-sm text-slate-600 mt-1.5">News, events &amp; community guide</p>
          <p className="mt-4 inline-block text-xs uppercase tracking-widest font-bold text-sky-600">You are here</p>
        </div>

        {/* 2 — paisleyscotland.com */}
        <a
          href="https://paisleyscotland.com"
          rel="noopener noreferrer"
          className="glass rounded-3xl p-7 text-center glass-hover group"
          data-testid="network-paisleyscotland-link"
        >
          <div className="w-14 h-14 rounded-2xl mx-auto bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center shadow-lg shadow-orange-500/30 ring-1 ring-white/40 mb-4">
            <Globe size={22} className="text-white" />
          </div>
          <p className="text-lg font-bold text-slate-900 tracking-tight group-hover:text-orange-600 transition-colors">paisleyscotland<span className="text-orange-500">.</span>com</p>
          <p className="text-sm text-slate-600 mt-1.5">Lifestyle, jobs &amp; business directory</p>
          <span className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-orange-600">
            Visit <ArrowUpRight size={11} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </a>

        {/* 3 — paisleymurals.co.uk — uses the brand logo */}
        <a
          href="https://paisleymurals.co.uk"
          rel="noopener noreferrer"
          className="glass rounded-3xl p-7 text-center glass-hover group"
          data-testid="network-paisleymurals-link"
        >
          <div className="w-14 h-14 rounded-2xl mx-auto bg-white flex items-center justify-center shadow-lg shadow-fuchsia-500/20 ring-1 ring-fuchsia-200/60 mb-4 overflow-hidden">
            <img
              src="/network-logos/paisley-murals.png"
              alt="Paisley Mural Trail"
              className="w-full h-full object-contain"
              loading="lazy"
            />
          </div>
          <p className="text-lg font-bold text-slate-900 tracking-tight group-hover:text-fuchsia-600 transition-colors">paisleymurals<span className="text-fuchsia-500">.</span>co<span className="text-slate-400">.uk</span></p>
          <p className="text-sm text-slate-600 mt-1.5">Street art &amp; the Mural Trail</p>
          <span className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-fuchsia-600">
            Visit <ArrowUpRight size={11} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </a>

        {/* 4 — clydeflow.com — uses the brand logo */}
        <a
          href="https://clydeflow.com"
          rel="noopener noreferrer"
          className="glass rounded-3xl p-7 text-center glass-hover group"
          data-testid="network-clydeflow-link"
        >
          <div className="w-14 h-14 rounded-2xl mx-auto bg-slate-950 flex items-center justify-center shadow-lg shadow-sky-500/20 ring-1 ring-sky-300/40 mb-4 overflow-hidden">
            <img
              src="/network-logos/clydeflow.png"
              alt="ClydeFlow"
              className="w-full h-full object-contain p-1"
              loading="lazy"
            />
          </div>
          <p className="text-lg font-bold text-slate-900 tracking-tight group-hover:text-sky-600 transition-colors">clydeflow<span className="text-sky-500">.</span>com</p>
          <p className="text-sm text-slate-600 mt-1.5">Flow better. Work smarter.</p>
          <span className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-sky-600">
            Visit <ArrowUpRight size={11} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </a>
      </div>
    </div>
  </section>
);

export default NetworkBadge;
